import React, { FC } from "react";
import { BoxCard } from "ui/componentes";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import MaterialTable from "material-table";
import {
    Championship,
    DriverStandings,
} from "api";
import { ArrowDownward, ArrowUpward, Remove } from "@material-ui/icons";
import { Box } from "@material-ui/core";
import {DateTime} from "luxon";
import { CsvBuilder } from "filefy";

interface DriversStandingsTableProps {
    championship: Championship;
    driversStandings: DriverStandings[];
}

const exportToCsv = (championship: Championship) => (
    columns,
    data
) => {
    const championshipName = championship.name.replace(/\s/g, "_").toLowerCase() || '';
    const fileName = `classificacao_pilotos_${
        championshipName
    }_${DateTime.local().toFormat("dd-MM-yyyy-HH-mm-ss")}.csv`;

    const titleColumns = columns
        .filter((c) => c.export === undefined || c.export)
        .map((c) => c.title);

    const rows = data.map((driver) => {
        const row: string[] = [];

        row.push(driver.position);
        row.push(driver.driverName);
        row.push(driver.teamName);
        row.push(driver.numberOfRounds);
        if (championship.isMultiClass) {
            row.push(driver.className);
        }
        row.push(driver.numberOfPoles);
        row.push(driver.wins);
        row.push(driver.top5);
        row.push(driver.points);

        return row;
    });

    new CsvBuilder(fileName)
        .setDelimeter(";")
        .setColumns(titleColumns)
        .addRows(rows)
        .exportFile();
};

const DriversStandingsTable: FC<DriversStandingsTableProps> = ({
    driversStandings,
    championship,
}) => {


    return (
        <MaterialTable
            components={{
                Container: BoxCard,
            }}
            title="Pilotos"
            data={driversStandings}
            columns={[
                {
                    field: "position",
                    title: "Pos.",
                    type: "numeric",
                    width: 30,
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                    render: ({ position, standingsByRound = [] }) => {
                        if (standingsByRound.length > 1) {
                            const lastStandings =
                                standingsByRound[standingsByRound.length - 1];
                            const standingsToCompare =
                                standingsByRound[standingsByRound.length - 2];

                            const changedPosition =
                                standingsToCompare.position -
                                lastStandings.position;

                            let Icon = Remove;

                            let color = "";
                            if (changedPosition > 0) {
                                Icon = ArrowUpward;
                                color = "green";
                            } else if (changedPosition < 0) {
                                Icon = ArrowDownward;
                                color = "red";
                            }

                            return (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize={9}
                                        color={color}
                                        marginRight={0.5}
                                    >
                                        (<Icon style={{ fontSize: 10 }} />
                                        {changedPosition !== 0
                                            ? changedPosition
                                            : null}
                                        )
                                    </Box>
                                    {position}
                                </Box>
                            );
                        }

                        return position;
                    },
                },
                {
                    field: "driverName",
                    title: "Piloto",
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "teamName",
                    title: "Equipe",
                    hidden: !championship.championshipByTeam,
                    export: championship.championshipByTeam,
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "numberOfRounds",
                    title: "Etapas (Baterias)",
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                    type: "numeric",
                    render: ({ numberOfRaces = 0, numberOfRounds = 0 }) => {
                        return `${numberOfRounds} (${numberOfRaces})`;
                    },
                },
                {
                    field: "className",
                    title: "Classe",
                    hidden: !championship.isMultiClass,
                    export: championship.isMultiClass,
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "numberOfPoles",
                    title: "Poles",
                    type: "numeric",
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "wins",
                    title: "Vitórias",
                    type: "numeric",
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "top5",
                    title: "Top5",
                    type: "numeric",
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "points",
                    title: "Pontuação",
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
            ]}
            options={{
                search: false,
                pageSize: 20,
                padding: "dense",
                headerStyle: { padding: "6px 8px 6px 8px" },
                exportDelimiter: ";",
                exportButton: true,
                exportCsv: exportToCsv(championship),
            }}
            localization={LocalizationPTBr}
        />
    );
};

export { DriversStandingsTable };
